import {
    BiAccordion,
    BiAccordionHeader,
    BiAccordionContent,
    BiLink,
    BiButton,
    BiList,
    BiListItem,
    BiNotification,
    BiTypography,
} from 'bi-web-components-react';
import { useState } from 'react';
import { BiaxCartItem } from '../../../types';
import { getTermTranslationKey } from '../../../utils';
import { useTranslation } from 'react-i18next';
import styles from './cartItem.module.scss';
import {
    BiIconCalendar,
    BiIconTrash,
    BiIconLocation,
    BiIconWorld,
    BiIconClock,
    BiIconSchool,
    BiIconLadder,
} from '@bi-digital/icons-react';

interface CartItemProps {
    item: BiaxCartItem;
    itemBeingDeleted: string | null;
    errorDeletingCartItem: boolean;
    removeFromCart: (item: BiaxCartItem) => void;
}

export default function CartItem({ item, itemBeingDeleted, errorDeletingCartItem, removeFromCart }: CartItemProps) {
    const { t, i18n } = useTranslation('cart');
    const {
        bannerId,
        residenceCode,
        campusCode,
        termCode,
        credits,
        language: teachingLanguage,
        studentType,
        name,
        productId,
    } = item;
    const [accordionItemExpanded, setAccordionItemExpanded] = useState(false);

    const campusText = t('cart:campus:' + campusCode);
    const [termTranslationKey, year] = getTermTranslationKey(termCode);
    const creditText = `${credits} ${t('cart:credits')}`;
    const isExecutiveStudy = studentType === '0';

    const residenceText = isExecutiveStudy ? '' : t('cart:residence:' + residenceCode);
    const studentTypeText = isExecutiveStudy ? '' : `${t('cart:studentypeText')}${t('cart:studentype' + studentType)}`;

    const handleAccordionItemChange = () => setAccordionItemExpanded(!accordionItemExpanded);

    return (
        <BiAccordion
            backgroundColor='blueSubtle'
            id={bannerId}
            onClick={() => handleAccordionItemChange()}
            aria-label={accordionItemExpanded ? t('common:expandLess') : t('common:expandMore')}
        >
            <BiAccordionHeader>
                <BiLink href={`/api/product-url?id=${productId}&lang=${i18n.language}`}>{name}</BiLink>
            </BiAccordionHeader>
            <BiAccordionContent>
                <span className='sr-only' role='presentation'>
                    {accordionItemExpanded ? `${t('common:expandLess')} ${name}` : `${t('common:expandMore')} ${name}`}
                </span>
                <BiList listId={`${bannerId}list`}>
                    <ListItem
                        text={residenceText}
                        icon={BiIconClock}
                        srOnly={t('cart:StudyLoadSrOnly') + residenceText}
                        expanded={accordionItemExpanded}
                    />
                    <ListItem
                        text={campusText}
                        icon={BiIconLocation}
                        srOnly={t('cart:LocationSrOnly') + campusText}
                        expanded={accordionItemExpanded}
                    />
                    <ListItem
                        text={t(termTranslationKey) + ' ' + year}
                        icon={BiIconCalendar}
                        srOnly={t('cart:StartDateSrOnly') + t(termTranslationKey) + ' ' + year}
                        expanded={accordionItemExpanded}
                    />
                    <ListItem
                        text={studentTypeText}
                        icon={BiIconLadder}
                        srOnly={t('cart:StudyStartSrOnly') + studentTypeText}
                        expanded={accordionItemExpanded}
                    />
                    <ListItem
                        text={creditText}
                        icon={BiIconSchool}
                        srOnly={t('cart:CreditsSrOnly') + creditText}
                        expanded={accordionItemExpanded}
                    />
                    <ListItem
                        text={teachingLanguage === 'no' ? t('cart:nolanguage') : t('cart:enlanguage')}
                        icon={BiIconWorld}
                        srOnly={
                            t('cart:LanguageSrOnly') + teachingLanguage === 'no'
                                ? t('cart:nolanguage')
                                : t('cart:enlanguage')
                        }
                        expanded={accordionItemExpanded}
                    />
                </BiList>
                {errorDeletingCartItem && (
                    <BiNotification icon='warning' color='red' heading={t('cart:errorHeading')}>
                        <BiTypography fontSize='medium'>{t('cart:errorDeletingCartItem')}</BiTypography>
                    </BiNotification>
                )}
                <div className={styles.removeButtonContainer}>
                    <BiButton
                        icon
                        variant='text'
                        submitting={itemBeingDeleted === bannerId}
                        onClick={() => removeFromCart(item)}
                        aria-label={t('cart:AriaLabelRemoveFromCart1') + name + t('cart:AriaLabelRemoveFromCart2')}
                    >
                        <BiIconTrash aria-hidden='true' />
                    </BiButton>
                </div>
            </BiAccordionContent>
        </BiAccordion>
    );
}

interface ListItemProps {
    text?: string;
    icon: React.ComponentType<Pick<HTMLBiIconClockElement, 'size' | 'color'>>;
    srOnly?: string;
    expanded: boolean;
}

function ListItem({ text, icon: Icon, srOnly, expanded }: ListItemProps) {
    return text ? (
        <BiListItem key={`${text}`}>
            {expanded ? (
                <span className='sr-only' role='presentation'>
                    {srOnly}
                </span>
            ) : null}
            <div className={styles.listItemContainer}>
                <Icon size='medium' color='gray' />
                <BiTypography color='grayStrong'>{text}</BiTypography>
            </div>
        </BiListItem>
    ) : null;
}
